import cn from 'classnames'
import { RequestStatus } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { useOpenRequest } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/hooks/useOpenRequest'
import { useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Skeleton } from 'shared/components/skeleton'

import { useGetOffboardingProcessPermissionQuery } from '../../api/mdOffboardingApi'
import { MDOffboardingRequestState } from '../../hooks/useMDOffboardingRequest'
import { TRANSLATION_KEYS } from '../translations'

import { MDOffboardingMetadata } from './components/mdOffboardingMetadata'
import { MDOffboardingRejectDialog } from './components/mdOffboardingRejectDialog'

import styles from './MDOffboarding.module.scss'

const getStatusLabel = (status?: string) => {
  switch (status) {
    case RequestStatus.New:
      return <Translation text={TRANSLATION_KEYS.inProgress} span />
    case RequestStatus.Done:
      return <Translation text={TRANSLATION_KEYS.completed} span />
    case RequestStatus.Rejected:
      return <Translation text={TRANSLATION_KEYS.rejected} span />
    default:
      return status
  }
}

interface IMDOffboardingProps {
  partnerId: string
  offboarding: MDOffboardingRequestState
}

export const MDOffboarding = ({ partnerId, offboarding }: IMDOffboardingProps) => {
  const {
    request,
    isLoading,
    loadError: requestLoadError,
    pendingAction,
    actionError,
    loadRequest,
    createRequest,
    rejectRequest,
    reopenRequest,
    clearActionError,
  } = offboarding
  const permission = useGetOffboardingProcessPermissionQuery(partnerId, { skip: !partnerId })
  const { openRequest } = useOpenRequest()
  const [showReject, setShowReject] = useState(false)
  const loading = isLoading || permission.isLoading
  const loadError = requestLoadError || permission.isError
  const status = request?.Status
  const statusLabel = getStatusLabel(status)

  const handleRetry = () => {
    loadRequest()
    permission.refetch()
  }

  const closeReject = () => {
    if (pendingAction !== 'reject') {
      setShowReject(false)
    }
  }

  return (
    <section className={styles.container}>
      <div className={styles.heading}>
        <h3>
          <Translation text={TRANSLATION_KEYS.title} span />
        </h3>
        {!loading && !loadError && request && (
          <span
            className={cn(styles.badge, {
              [styles.inProgress]: status === RequestStatus.New,
              [styles.completed]: status === RequestStatus.Done,
              [styles.rejected]: status === RequestStatus.Rejected,
            })}
          >
            {statusLabel}
          </span>
        )}
      </div>

      {loading ? (
        <div className={styles.skeleton}>
          <Skeleton width="78%" height={12} border="5px" />
          <Skeleton width="52%" height={12} border="5px" />
          <Skeleton className={styles.skeletonButton} width={165} height={34} border="5px" />
        </div>
      ) : loadError ? (
        <>
          <p className={styles.error}>
            <Translation text={TRANSLATION_KEYS.loadError} span />
          </p>
          <div className={styles.actions}>
            <Button
              variant="outlined"
              loading={isLoading || permission.isFetching}
              disabled={isLoading || permission.isFetching}
              onClick={handleRetry}
            >
              <Translation text={TRANSLATION_KEYS.retry} span />
            </Button>
          </div>
        </>
      ) : request ? (
        <>
          <p className={styles.description}>
            <MDOffboardingMetadata request={request} />
          </p>
          {status === RequestStatus.Rejected && request.RejectReason && (
            <p className={styles.description}>
              <Translation
                text={TRANSLATION_KEYS.rejectReason}
                span
                skipInterpolation
                replaceParams={[{ key: '{{reason}}', value: request.RejectReason }]}
              />
            </p>
          )}
          <div className={styles.actions}>
            <Button variant="outlined" onClick={() => openRequest({ item: request })}>
              <Translation text={TRANSLATION_KEYS.viewDetails} span />
            </Button>
            {status === RequestStatus.Rejected && permission.data && (
              <Button
                loading={pendingAction === 'reopen'}
                disabled={pendingAction === 'reopen' || isLoading}
                onClick={() => reopenRequest()}
              >
                <Translation text={TRANSLATION_KEYS.reopen} span />
              </Button>
            )}
            {status === RequestStatus.New && permission.data && (
              <Button
                variant="text"
                color="danger"
                disabled={isLoading || pendingAction !== null}
                onClick={() => {
                  clearActionError()
                  setShowReject(true)
                }}
              >
                <Translation text={TRANSLATION_KEYS.reject} span />
              </Button>
            )}
          </div>
          {actionError === 'reopen' && (
            <p className={styles.error}>
              <Translation text={TRANSLATION_KEYS.reopenError} span />
            </p>
          )}
        </>
      ) : (
        <>
          <p className={styles.description}>
            <Translation text={TRANSLATION_KEYS.description} span />
          </p>
          {permission.data ? (
            <div className={styles.actions}>
              <Button
                loading={pendingAction === 'create'}
                disabled={pendingAction === 'create' || isLoading}
                onClick={() => createRequest()}
              >
                <Translation text={TRANSLATION_KEYS.start} span />
              </Button>
              {pendingAction === 'create' && (
                <span className={styles.description}>
                  <Translation text={TRANSLATION_KEYS.creating} span />
                </span>
              )}
              {actionError === 'create' && (
                <span className={styles.error}>
                  <Translation text={TRANSLATION_KEYS.createError} span />
                </span>
              )}
            </div>
          ) : (
            <p className={styles.description}>
              <Translation text={TRANSLATION_KEYS.noPermission} span />
            </p>
          )}
        </>
      )}
      <MDOffboardingRejectDialog
        show={showReject}
        onCancel={closeReject}
        onReject={async (comment) => {
          const success = await rejectRequest(comment)
          if (success) {
            setShowReject(false)
          }
        }}
        isPending={pendingAction === 'reject'}
        isError={actionError === 'reject'}
      />
    </section>
  )
}
